import { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import { ButtonCustom } from "../../../components/form/controls/form.button";
import { AddJobModal } from "../../../components/app/job/add-job.modal";
import { JobFilterModal } from "../../../components/app/job/job-filter.modal";
import { jobService } from "../../../service/job/job.service";
import { convertIsoTime } from "../../../util/date.util";
import { formatCurrency } from "../../../util/number.util";
import { useNavigate } from "react-router-dom";
import { PageTitle } from "../../../components/shared/PageTitle";
import { ToggleInput } from "../../../components/form/controls/toggle.input";
import { JobStatus } from "../../../models/enum/job-status.enum";
import { StatusPickerInput } from "../../../components/form/controls/status-picker.input";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import PopoverCustom from "../../../components/shared/PopoverCustom";
import { TableContainer } from "../../../components/shared/table/TableContainer";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Loading } from "../../../components/shared/Loading";
import { JobType } from "../../../models/enum/job-type.enum";
import { TableFooter } from "../../../components/shared/table/TableFooter";
import inventoryService from "../../../service/inventory/inventory.service";

interface Column {
  header: string;
  width?: string;
  accessor: string;
  callback?: (arg0?: any, arg1?: any) => React.ReactNode;
}

export const Job = () => {
  const DEFAULT_COLUMNS: Column[] = [
    {
      header: "Ref #",
      width: "75px",
      accessor: "jobNo",
      callback: (jobNo: string, job: any) => (
        <Text
          fontWeight="600"
          color="brand.green"
          cursor="pointer"
          onClick={() => navigate(`/app/job/${job.id}`)}
        >{`${jobNo}`}</Text>
      ),
    },
    {
      header: "Company name",
      accessor: "companyName",
      width: "200px",
      callback: (companyName: string, job: any) => (
        <Text
          fontWeight="600"
          color="brand.green"
          cursor="pointer"
          onClick={() => navigate(`/app/company/${job.companyId}`)}
        >{`${companyName}`}</Text>
      ),
    },
    {
      header: "Contact",
      width: "200px",
      accessor: "contactEmail",
    },
    {
      header: "No. items",
      width: "125px",
      accessor: "totalInventory",
      callback: (totalInventory: string) => (
        <>{+totalInventory === 0 ? "-" : totalInventory}</>
      ),
    },
    {
      header: "Created",
      width: "125px",
      accessor: "createdDate",
    },
    {
      header: "Completed",
      width: "125px",
      accessor: "statusUpdatedDate",
    },
    {
      header: "Total quote",
      width: "150px",
      accessor: "totalQuotePrice",
      callback: (price: number) => <>{price ? <Box>${price}</Box> : ""}</>,
    },
    {
      header: "Total rebate",
      width: "150px",
      accessor: "totalRebate",
      callback: (rebate: number) => <>{rebate ? <Box>${rebate}</Box> : ""}</>,
    },
    {
      header: "Total sale",
      width: "150px",
      accessor: "totalSalePrice",
      callback: (price: number) => (
        <>{price ? <Box fontWeight="bold">${price}</Box> : ""}</>
      ),
    },
    {
      header: "Status",
      width: "125px",
      accessor: "status",
      callback: (status: string, job: any) => (
        <StatusPickerInput
          key={job.id}
          id={job.id}
          status={status}
          options={JobStatus}
          onClick={handleStatusUpdate}
        />
      ),
    },
    {
      header: "Notes",
      width: "300px",
      accessor: "notes",
      callback: (notes) =>
        notes?.length && (
          <PopoverCustom
            width="300px"
            trigger={
              <Box
                display="flex"
                alignItems="center"
                height="100%"
                px={6}
                _hover={{
                  bgColor: "gray.200",
                }}
                cursor="pointer"
              >
                <Text
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {notes}
                </Text>
              </Box>
            }
          >
            <Text whiteSpace="normal">{notes}</Text>
          </PopoverCustom>
        ),
    },
  ];

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [jobs, setJobs] = useState({
    data: [] as any[],
    totalItems: 0,
    totalPage: 0,
  });
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tableColumns, setTableColumns] = useState<Column[]>(DEFAULT_COLUMNS);
  const [toggled, setToggled] = useState(false);
  const [metrics, setMetrics] = useState<{
    totalRebate: number;
    totalSalePrice: number;
    totalQuotePrice: number;
    totalInventory: number;
  } | null>(null);
  const [jobFilter, setJobFilter] = useState({} as any);
  const jobFilterRef = useRef(jobFilter);

  const { sortColumn } = useSelector((state: RootState) => state.job);
  const { region } = useSelector((state: RootState) => state.userProfile);

  const handleStatusUpdate = async (id: string, status: string) => {
    await jobService.updateStatus({ id, status }).then(() => {
      let data = [...jobs.data].map((job) => {
        if (job.id === id) {
          return { ...job, status };
        }
        return job;
      });

      // if (sortColumn?.accessor?.length)
      //   data = sortDirection(data, sortColumn.accessor, sortColumn.direction);

      setJobs({ ...jobs, data });
    });
  };

  const handleSort = (props: { accessor: string; direction: 0 | 1 }) => {
    // dispatch(
    //   update({
    //     sortColumn: { accessor: props.accessor, direction: props.direction },
    //   })
    // );

    // setPageNum(1);
    console.log("Sort not implemented. Skipping...");
  };

  const fetchMetrics = async (filter?: any, region?: "AU" | "PH") => {
    if (toggled) {
      try {
        const metricsData = await inventoryService.getMetrics({
          jobStatus: filter?.status,
          jobStatusDateRange: filter?.statusDateRange,
          jobType: JobType.INVENTORY,
        }, region);

        setMetrics(metricsData);
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetrics(null);
      }
    }
  };

  const init = async (page?: number) => {
    setLoading(true);
    const filter = {
      incompleteOnly: !toggled,
      status: toggled ? "COMPLETE" : undefined,
      type: JobType.INVENTORY,
      statusDateRange: jobFilter?.statusDateRange,
    };

    // Only allow filtering by status date range for complete jobs
    if (!toggled) {
      filter.statusDateRange = undefined;
    }

    await jobService
      .getList(filter, region, page ?? pageNum, pageSize, sortColumn)
      .then((response) => {
        let data = response.data.map((job) => {
          const resp = {
            ...job,
            createdDate: convertIsoTime(job.createdDate),
          } as any;

          if (job.statusUpdatedDate) {
            resp.statusUpdatedDate = convertIsoTime(job.statusUpdatedDate);
          }

          return resp;
        });

        setJobs({
          data,
          totalItems: response.totalItems,
          totalPage: response.totalPage,
        });
      })
      .finally(() => {
        setLoading(false);
      });

    if (toggled) {
      fetchMetrics(filter, region);
    }
  };

  useEffect(() => {
    if (toggled) {
      setTableColumns(
        DEFAULT_COLUMNS.filter(
          (column) =>
            column.accessor !== "totalQuotePrice" &&
            column.accessor !== "status"
        )
      );
    } else {
      setTableColumns(
        DEFAULT_COLUMNS.filter(
          (column) =>
            column.accessor !== "totalRebate" &&
            column.accessor !== "statusUpdatedDate"
        )
      );
    }
  }, [toggled]);

  useEffect(() => {
    jobFilterRef.current = jobFilter;
    setPageNum(1);
    init(1);
  }, [jobFilter]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize, sortColumn, toggled, region]);

  useEffect(() => {
    if (toggled) {
      fetchMetrics();
    }
  }, [toggled]);

  return (
    <>
      <Loading loading={loading} />
      {/* this is just a placeholder - change to Chakra table or similar */}
      <Flex flexDirection="column">
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <PageTitle>Jobs</PageTitle>

          <Flex flexDirection="row" gap={2}>
            <Flex flexDirection="row" justifyContent="flex-end">
              <ToggleInput
                leftLabel="In progress"
                rightLabel="Complete"
                onChange={(e) => {
                  setToggled(e.target.checked);
                }}
              />
            </Flex>
            {toggled && (
              <ButtonCustom secondary onClick={() => setFilterModalOpen(true)}>
                <FilterListIcon />
                Filter
              </ButtonCustom>
            )}
            <ButtonCustom onClick={() => setModalOpen(true)}>
              <AddIcon />
              Add job
            </ButtonCustom>
          </Flex>
        </Flex>
      </Flex>

      {/* Metrics Card */}
      {toggled && metrics && (
        <Flex
          flexDirection="row"
          gap={4}
          mb={4}
          p={4}
          bg="white"
          borderRadius="md"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Lifetime Rebate
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              ${formatCurrency(metrics.totalRebate || 0)}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Lifetime Sales
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              ${formatCurrency(metrics.totalSalePrice || 0)}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Lifetime Quotes
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              ${formatCurrency(metrics.totalQuotePrice || 0)}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Completed Jobs
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              {jobs.totalItems || 0}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Total Inventory
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              {metrics.totalInventory || 0}
            </Text>
          </Box>
        </Flex>
      )}

      <Flex flexDirection="column">
        <TableContainer
          columns={tableColumns}
          onSort={handleSort}
          sortColumn={sortColumn}
        >
          <Tbody zIndex={9}>
            {jobs.data.map((row, i) => (
              <Tr key={i} height="100%">
                {tableColumns.map((column, j) => (
                  <Td
                    key={j}
                    height="40px"
                    width={column.width ?? "full"}
                    p={column.accessor === "notes" && 0}
                  >
                    {!column.callback
                      ? row[column.accessor]
                      : column.callback(row[column.accessor], row)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </TableContainer>
        <TableFooter
          count={jobs.data?.length}
          totalItems={jobs.totalItems}
          totalPage={jobs.totalPage}
          pageNum={pageNum}
          onChange={(num) => {
            setPageNum(num);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageNum(1);
            setPageSize(size);
          }}
        />
      </Flex>
      <AddJobModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={() => {
          setPageNum(1);
        }}
      />
      <JobFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onSubmit={(filter) => {
          setJobFilter(filter);
        }}
        onClear={() => {
          setJobFilter({});
        }}
      />
    </>
  );
};
